import {
  DRAFT_STAGE_LABEL,
  draftStage,
  feedSaleStateFromAuctionStatus,
  fetchAuctionDrafts,
  fetchHouseContact,
  withLiveFigures,
  type LotDraft,
  type PublicLotRow,
} from '@auctisync/api-client';
import { Badge, Button, HelpTooltip, LearnMoreLink } from '@auctisync/ui';
import {
  COMMON_TIME_ZONES,
  detectBrowserTimeZone,
  dollarsToCents,
  formatCents,
  isoToZonedLocal,
  resolveTimeZone,
  zoneAbbreviation,
  zonedLocalToIso,
  type Tables,
  type TablesInsert,
} from '@auctisync/shared';
import { AUCTION_ROW_COLUMNS, type AuctionRow } from '../../lib/rowColumns';
import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { AddLotsFromInventory } from '../../components/AddLotsFromInventory';
import { AdminPreviewEvents } from '../../components/AdminPreviewEvents';
import { ConsignorSettlementPanel } from './ConsignorSettlementPanel';
import { CreateEmptyLotsModal } from '../../components/CreateEmptyLotsModal';
import { LiveStatusBanner } from '../../components/bidder-web/LiveStatusBanner';
import { useLiveSource } from '../../hooks/useLiveSource';
import { useLotSubscription } from '../../hooks/useLotSubscription';
import { useSaleFeed } from '../../hooks/useSaleFeed';
import { readClaims, useSession } from '../../hooks/useSession';
import { supabase } from '../../lib/supabase';
import { useToast } from '../../components/Toast';
import {
  EMPTY_WINDOW_ROW,
  validatePickupWindows,
  windowRowsFromJson,
  windowRowsToJson,
  type PickupWindowRow,
} from '../../lib/pickupWindows';

type Auction = AuctionRow;
type AuctionCategory = Pick<Tables<'auction_categories'>, 'id' | 'name'>;
type BidScale = Pick<Tables<'bid_increment_scales'>, 'id' | 'name' | 'is_default'>;
type ProfileOption = { id: string; name: string; is_default: boolean };
type TenantOption = Pick<Tables<'tenants'>, 'id' | 'slug' | 'name'>;
type LotRow = Pick<
  Tables<'lots'>,
  | 'id'
  | 'lot_number'
  | 'title'
  | 'starting_bid_cents'
  | 'current_bid_cents'
  | 'bid_count'
  | 'status'
  | 'quantity'
  | 'cataloged_at'
  | 'control_number'
  | 'added_in_warehouse_at'
>;

const AUCTION_TYPES = [
  'timed_online',
  'live_simulcast',
  'live_floor',
  'fundraiser',
  'private_treaty',
] as const;
// 'closing' is intentionally absent: since the bidding-gate model it is a
// DERIVED urgency state (computed from time-to-close), never stored.
const STATUSES = [
  'draft',
  'queued',
  'preview',
  'active',
  'completed',
  'archived',
  'cancelled',
] as const;
const LOCK_MODES = ['increment_absolute', 'any_higher_amount'] as const;
const EXTENSION_METHODS = ['reset', 'extend'] as const;

type FormState = {
  title: string;
  slug: string;
  reference_number: string;
  description: string;
  preview_text: string;
  auction_type: string;
  category_id: string;
  starts_at: string;
  ends_at: string;
  preview_starts_at: string;
  preview_ends_at: string;
  stagger_qty: string;
  stagger_seconds: string;
  extension_threshold_seconds: string;
  extension_amount_seconds: string;
  extension_method: string;
  default_starting_bid: string;
  default_bid_increment_scale_id: string;
  consignment_profile_id: string;
  tax_profile_id: string;
  lock_bid_amount: string;
  buyers_premium_pct: string;
  online_premium_pct: string;
  allow_preview_bidding: boolean;
  requires_bidder_approval: boolean;
  requires_terms_acceptance: boolean;
  requires_cc_preauth: boolean;
  cc_preauth_amount: string;
  hide_unsold_lots: boolean;
  hide_winning_info: boolean;
  hide_bid_history: boolean;
  status: string;
  is_visible: boolean;
  photo_file_prefix: string;
  /**
   * The zone every date on this form is typed and read in.
   *
   * 🔴 NOT THE BROWSER'S. Jim, 2026-09-18: *"When I am traveling, I have to do
   * calculations to make sure I am entering the right auction times."* A sale
   * closes at 6pm at the warehouse whoever is looking, so the sale carries the
   * zone and the form converts at its edges.
   */
  timezone: string;
  /*
   * Collection. Per-sale on purpose, not per-house: a sale can move premises
   * for one auction, and the style of auction decides how collection runs.
   * That is why these are auction columns and why the scheduler lives on this
   * form rather than in onboarding.
   */
  pickup_location: string;
  pickup_address_line1: string;
  pickup_address_line2: string;
  pickup_city: string;
  pickup_region: string;
  pickup_postal_code: string;
  pickup_instructions: string;
  pickup_windows: PickupWindowRow[];
};

const EMPTY_FORM: FormState = {
  title: '',
  slug: '',
  reference_number: '',
  description: '',
  preview_text: '',
  auction_type: 'timed_online',
  category_id: '',
  starts_at: '',
  ends_at: '',
  preview_starts_at: '',
  preview_ends_at: '',
  stagger_qty: '6',
  stagger_seconds: '60',
  extension_threshold_seconds: '300',
  extension_amount_seconds: '300',
  extension_method: 'reset',
  default_starting_bid: '1.00',
  default_bid_increment_scale_id: '',
  consignment_profile_id: '',
  tax_profile_id: '',
  lock_bid_amount: 'increment_absolute',
  buyers_premium_pct: '15.00',
  online_premium_pct: '',
  allow_preview_bidding: false,
  requires_bidder_approval: false,
  requires_terms_acceptance: true,
  requires_cc_preauth: false,
  cc_preauth_amount: '',
  // Matches the column defaults in 20260908120000: unsold lots are hidden until
  // a house chooses to publish them; the two privacy flags start permissive.
  hide_unsold_lots: true,
  hide_winning_info: false,
  hide_bid_history: false,
  status: 'draft',
  is_visible: true,
  photo_file_prefix: '',
  pickup_location: '',
  pickup_address_line1: '',
  pickup_address_line2: '',
  pickup_city: '',
  pickup_region: '',
  pickup_postal_code: '',
  pickup_instructions: '',
  // Filled from the house's own zone when the form mounts — never left to the
  // browser, which is the exact thing that made this annoying.
  timezone: '',
  // No blank starter row: an empty schedule is a legitimate state (plenty of
  // sales say only "Sat 9–4" in prose), and a row sitting there unfilled looks
  // like something the auctioneer forgot rather than something they declined.
  pickup_windows: [],
};

function slugify(input: string): string {
  return input
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 80);
}

/*
 * `isoToLocal` / `localToIso` used to live here, untested, and `localToIso`
 * trusted `new Date(string)`. They now come from `lib/pickupWindows`, where a
 * test proved that trust misplaced: `new Date('Sat 9-4')` returns 4 September
 * 2001 rather than failing. The schedule fields and the four date fields below
 * share one implementation so they cannot drift.
 */

function auctionToForm(a: Auction, houseTimeZone: string | null): FormState {
  // The sale's own zone wins; a sale that has never been asked falls back to the
  // house's, then to the platform default. Resolved ONCE here so every field
  // below reads the same clock.
  const tz = resolveTimeZone(a.timezone, houseTimeZone);
  return {
    title: a.title,
    slug: a.slug,
    reference_number: a.reference_number === null ? '' : String(a.reference_number),
    description: a.description ?? '',
    preview_text: a.preview_text ?? '',
    auction_type: a.auction_type,
    category_id: a.category_id ?? '',
    starts_at: isoToZonedLocal(a.starts_at, tz),
    ends_at: isoToZonedLocal(a.ends_at, tz),
    preview_starts_at: isoToZonedLocal(a.preview_starts_at, tz),
    preview_ends_at: isoToZonedLocal(a.preview_ends_at, tz),
    stagger_qty: String(a.stagger_qty ?? 0),
    stagger_seconds: String(a.stagger_seconds ?? 0),
    extension_threshold_seconds: String(a.extension_threshold_seconds),
    extension_amount_seconds: String(a.extension_amount_seconds),
    extension_method: a.extension_method,
    default_starting_bid:
      a.default_starting_bid_cents === null ? '' : (a.default_starting_bid_cents / 100).toFixed(2),
    default_bid_increment_scale_id: a.default_bid_increment_scale_id ?? '',
    consignment_profile_id: a.consignment_profile_id ?? '',
    tax_profile_id: a.tax_profile_id ?? '',
    lock_bid_amount: a.lock_bid_amount ?? 'increment_absolute',
    buyers_premium_pct: (a.buyers_premium_bps / 100).toFixed(2),
    online_premium_pct:
      a.online_premium_bps === null ? '' : (a.online_premium_bps / 100).toFixed(2),
    allow_preview_bidding: a.allow_preview_bidding,
    requires_bidder_approval: a.requires_bidder_approval,
    requires_terms_acceptance: a.requires_terms_acceptance,
    requires_cc_preauth: a.requires_cc_preauth,
    cc_preauth_amount:
      a.cc_preauth_amount_cents === null ? '' : (a.cc_preauth_amount_cents / 100).toFixed(2),
    hide_unsold_lots: a.hide_unsold_lots,
    hide_winning_info: a.hide_winning_info,
    hide_bid_history: a.hide_bid_history,
    status: a.status,
    is_visible: a.is_visible,
    photo_file_prefix: a.photo_file_prefix ?? '',
    pickup_location: a.pickup_location ?? '',
    pickup_address_line1: a.pickup_address_line1 ?? '',
    pickup_address_line2: a.pickup_address_line2 ?? '',
    pickup_city: a.pickup_city ?? '',
    pickup_region: a.pickup_region ?? '',
    pickup_postal_code: a.pickup_postal_code ?? '',
    pickup_instructions: a.pickup_instructions ?? '',
    timezone: tz,
    pickup_windows: windowRowsFromJson(a.pickup_dates, tz),
  };
}

function formToRow(form: FormState, tenantId: string): TablesInsert<'auctions'> {
  const s = (v: string) => (v.trim() === '' ? null : v.trim());
  // Every wall clock on this form is in the sale's zone, so every conversion
  // out of it uses that zone and nothing else.
  const tz = resolveTimeZone(form.timezone);
  const toIso = (local: string) => zonedLocalToIso(local, tz);
  const pctToBps = (v: string) => (v.trim() === '' ? null : Math.round(Number(v) * 100));
  const parseInt0 = (v: string) => {
    const n = parseInt(v.trim() || '0', 10);
    return Number.isFinite(n) ? n : 0;
  };
  const startingBid =
    form.default_starting_bid.trim() === ''
      ? null
      : dollarsToCents(Number(form.default_starting_bid));

  return {
    tenant_id: tenantId,
    title: form.title.trim(),
    slug: form.slug.trim() || slugify(form.title),
    reference_number: form.reference_number.trim()
      ? parseInt(form.reference_number.trim(), 10)
      : null,
    description: s(form.description),
    preview_text: s(form.preview_text),
    auction_type: form.auction_type,
    category_id: s(form.category_id),
    starts_at: toIso(form.starts_at),
    ends_at: toIso(form.ends_at),
    preview_starts_at: toIso(form.preview_starts_at),
    preview_ends_at: toIso(form.preview_ends_at),
    stagger_qty: parseInt0(form.stagger_qty),
    stagger_seconds: parseInt0(form.stagger_seconds),
    extension_threshold_seconds: parseInt0(form.extension_threshold_seconds),
    extension_amount_seconds: parseInt0(form.extension_amount_seconds),
    extension_method: form.extension_method,
    default_starting_bid_cents: startingBid,
    default_bid_increment_scale_id: s(form.default_bid_increment_scale_id),
    consignment_profile_id: s(form.consignment_profile_id),
    tax_profile_id: s(form.tax_profile_id),
    lock_bid_amount: form.lock_bid_amount,
    buyers_premium_bps: pctToBps(form.buyers_premium_pct) ?? 1500,
    online_premium_bps: pctToBps(form.online_premium_pct),
    allow_preview_bidding: form.allow_preview_bidding,
    requires_bidder_approval: form.requires_bidder_approval,
    requires_terms_acceptance: form.requires_terms_acceptance,
    requires_cc_preauth: form.requires_cc_preauth,
    cc_preauth_amount_cents:
      form.cc_preauth_amount.trim() === '' ? null : dollarsToCents(Number(form.cc_preauth_amount)),
    hide_unsold_lots: form.hide_unsold_lots,
    hide_winning_info: form.hide_winning_info,
    hide_bid_history: form.hide_bid_history,
    status: form.status,
    is_visible: form.is_visible,
    photo_file_prefix: s(form.photo_file_prefix),
    pickup_location: s(form.pickup_location),
    pickup_address_line1: s(form.pickup_address_line1),
    pickup_address_line2: s(form.pickup_address_line2),
    pickup_city: s(form.pickup_city),
    pickup_region: s(form.pickup_region),
    pickup_postal_code: s(form.pickup_postal_code),
    pickup_instructions: s(form.pickup_instructions),
    timezone: tz,
    pickup_dates: windowRowsToJson(form.pickup_windows, tz),
  };
}

export function AdminAuctionDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { session } = useSession();
  const claims = readClaims(session);
  const toast = useToast();
  const isCreate = id === 'new';

  const [form, setForm] = useState<FormState>(EMPTY_FORM);
  const [auction, setAuction] = useState<Auction | null>(null);
  const [loading, setLoading] = useState(!isCreate);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [categories, setCategories] = useState<AuctionCategory[]>([]);
  const [scales, setScales] = useState<BidScale[]>([]);
  const [consignProfiles, setConsignProfiles] = useState<ProfileOption[]>([]);
  const [taxProfiles, setTaxProfiles] = useState<ProfileOption[]>([]);

  /*
   * The house's own zone, used as the default for a NEW sale and as the
   * fallback for one that predates this field.
   *
   * 🔴 THE BROWSER IS THE LAST RESORT, NOT THE FIRST. Seeding a new sale from
   * the machine is exactly the bug Jim described — create a sale from a hotel
   * and it would silently be in the hotel's zone. It is used only when the
   * house has not said, which is a one-time state ending the moment they do.
   */
  const [houseTimeZone, setHouseTimeZone] = useState<string | null>(null);

  const [tenantOptions, setTenantOptions] = useState<TenantOption[]>([]);
  const [selectedTenantId, setSelectedTenantId] = useState<string>(claims.tenantId ?? '');
  const needsTenantPicker = claims.isSuperAdmin && !claims.tenantId;

  const [lots, setLots] = useState<LotRow[]>([]);
  const [lotsLoading, setLotsLoading] = useState(false);
  const [showAddLots, setShowAddLots] = useState(false);
  const [showCreateEmpty, setShowCreateEmpty] = useState(false);
  const [lotFilter, setLotFilter] = useState<
    'all' | 'needs-research' | 'needs-photos' | 'prices-to-review' | 'from-warehouse'
  >('all');
  // The cataloguing AI draft per lot, for the "AI draft" column and the
  // "Prices to review" filter. One query for the whole sale.
  const [drafts, setDrafts] = useState<Map<string, LotDraft>>(new Map());

  useEffect(() => {
    void (async () => {
      const [{ data: cats }, { data: scs }] = await Promise.all([
        supabase
          .from('auction_categories')
          .select('id, name')
          .is('archived_at', null)
          .order('sort_order', { ascending: true })
          .order('name', { ascending: true }),
        supabase
          .from('bid_increment_scales')
          .select('id, name, is_default')
          .is('archived_at', null)
          .order('is_default', { ascending: false })
          .order('name', { ascending: true }),
      ]);
      setCategories(cats ?? []);
      setScales(scs ?? []);
    })();
  }, []);

  // Profile dropdowns are scoped to the auction's tenant (a super admin
  // otherwise sees — and could persist — another tenant's profile id). Reloads
  // when the auction loads (edit) or the super admin picks a tenant (create).
  const scopeTenantId = !isCreate
    ? (auction?.tenant_id ?? claims.tenantId ?? '')
    : (claims.tenantId ?? selectedTenantId);
  useEffect(() => {
    if (!scopeTenantId) return;
    let cancelled = false;
    void (async () => {
      const [{ data: consign }, { data: tax }] = await Promise.all([
        supabase
          .from('consignment_profiles')
          .select('id, name, is_default')
          .eq('tenant_id', scopeTenantId)
          .is('archived_at', null)
          .order('is_default', { ascending: false })
          .order('name', { ascending: true }),
        supabase
          .from('tax_profiles')
          .select('id, name, is_default')
          .eq('tenant_id', scopeTenantId)
          .is('archived_at', null)
          .order('is_default', { ascending: false })
          .order('name', { ascending: true }),
      ]);
      if (cancelled) return;
      setConsignProfiles(consign ?? []);
      setTaxProfiles(tax ?? []);
    })();
    return () => {
      cancelled = true;
    };
  }, [scopeTenantId]);

  useEffect(() => {
    if (!isCreate || !scopeTenantId) return;
    let cancelled = false;
    void (async () => {
      const contact = await fetchHouseContact(supabase, scopeTenantId);
      if (cancelled) return;
      setHouseTimeZone(contact?.timezone ?? null);
    })();
    return () => {
      cancelled = true;
    };
  }, [isCreate, scopeTenantId]);

  /*
   * A NEW sale takes the house's zone the moment it is known. Guarded on the
   * field still being blank so it can never overwrite a zone the person has
   * already picked while the fetch was in flight.
   */
  useEffect(() => {
    if (!isCreate) return;
    setForm((f) =>
      f.timezone === ''
        ? { ...f, timezone: resolveTimeZone(houseTimeZone, detectBrowserTimeZone()) }
        : f,
    );
  }, [isCreate, houseTimeZone]);

  useEffect(() => {
    if (!needsTenantPicker) return;
    void (async () => {
      const { data } = await supabase
        .from('tenants')
        .select('id, slug, name')
        .eq('status', 'active')
        .order('name', { ascending: true });
      const list = data ?? [];
      setTenantOptions(list);
      const first = list[0];
      if (first && !selectedTenantId) setSelectedTenantId(first.id);
    })();
  }, [needsTenantPicker, selectedTenantId]);

  useEffect(() => {
    if (isCreate || !id) return;
    let cancelled = false;
    setLoading(true);
    void (async () => {
      const { data, error: err } = await supabase
        .from('auctions')
        .select(AUCTION_ROW_COLUMNS)
        .eq('id', id)
        .maybeSingle();
      if (cancelled) return;
      if (err) setError(err.message);
      else if (!data) setError('Auction not found.');
      else {
        /*
         * 🔴 THE HOUSE ZONE IS FETCHED BEFORE THE FORM HYDRATES, not alongside
         * it. A sale that predates `auctions.timezone` falls back to the
         * house's, so resolving it late would paint every date against the
         * platform default and then move them — and a person who hit Save in
         * that window would store the times they had NOT seen.
         */
        const contact = await fetchHouseContact(supabase, data.tenant_id);
        if (cancelled) return;
        const tz = contact?.timezone ?? null;
        setHouseTimeZone(tz);
        setAuction(data);
        setForm(auctionToForm(data, tz));
      }
      setLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [id, isCreate]);

  const refreshLots = useCallback(async () => {
    if (!auction) return;
    setLotsLoading(true);
    // `lots_staff`: `added_in_warehouse_at` is the house's, hidden from the
    // shared roles on `lots` (20261005120000). The office reads lots here.
    const { data, error: err } = await supabase
      .from('lots_staff')
      .select(
        'id, lot_number, title, starting_bid_cents, current_bid_cents, bid_count, status, quantity, cataloged_at, control_number, added_in_warehouse_at',
      )
      .eq('auction_id', auction.id)
      .order('lot_number', { ascending: true });
    if (!err) setLots((data ?? []) as LotRow[]);
    try {
      const rows = await fetchAuctionDrafts(supabase, auction.id);
      setDrafts(new Map(rows.map((d) => [d.lot_id, d])));
    } catch {
      // The draft column is a guide, not the list. A failed read shows the
      // lots without it rather than no lots at all.
      setDrafts(new Map());
    }
    setLotsLoading(false);
  }, [auction]);

  useEffect(() => {
    if (auction) void refreshLots();
  }, [auction, refreshLots]);

  /**
   * 🔴 THE LOT TABLE IS LIVE (2026-10-08). Jim: the house's live views move to
   * the engine with everything else. On a sale the engine runs, the house's
   * staff feed carries EVERY lot (the engine decides that from who is signed
   * in, never this page): one summary when the page opens, then each change.
   * The current bid, bid count and status columns read it
   * (`withLiveFigures`); everything else in the row is the catalogue's. A sale
   * on the Supabase book keeps Realtime. `useLiveSource` decides which.
   */
  const { source } = useLiveSource(lots[0]?.id ?? null);
  const live = useSaleFeed(source, {
    summary: true,
    initialSaleState: feedSaleStateFromAuctionStatus(auction?.status),
  });
  const onRealtimeRow = useCallback((row: PublicLotRow) => {
    setLots((prev) =>
      prev.map((l) =>
        l.id === row.id
          ? {
              ...l,
              current_bid_cents: row.current_bid_cents,
              bid_count: row.bid_count,
              status: row.status,
            }
          : l,
      ),
    );
  }, []);
  useLotSubscription({
    target: { kind: 'auction', auctionId: auction?.id ?? '' },
    onUpdate: onRealtimeRow,
    enabled: source?.kind === 'supabase' && auction !== null,
  });
  const liveFeed = live.feed;
  const shownLots = useMemo(
    () => lots.map((l) => withLiveFigures(l, liveFeed?.lot(l.id))),
    // `live.version` re-reads the feed.
    [lots, liveFeed, live.version],
  );

  /**
   * Move every DRAFT lot in this auction to SCHEDULED, in one write.
   *
   * 🔴 WHY THIS EXISTS. Every lot is created `draft`, and going live only
   * promotes `scheduled` → `active` (migration 20260701140000). Nothing moved
   * lots off `draft` in bulk, so the only route was the per-lot status dropdown:
   * a 200-lot sale meant 200 trips through a detail page, and any lot missed on
   * the way SILENTLY DID NOT SELL. Three of six pre-DTA reviews called it
   * blocking for a real auctioneer's first sale, and they were right — the
   * failure is quiet, which is the worst kind on a sale day.
   *
   * Scoped to this auction AND to `draft`, so it can never touch a lot that is
   * already scheduled, live, sold or cancelled. `.select('id')` makes the
   * database report what it actually changed rather than this code assuming.
   */
  const [promotingDrafts, setPromotingDrafts] = useState(false);
  const draftLots = useMemo(() => lots.filter((l) => l.status === 'draft'), [lots]);

  const promoteDraftLots = useCallback(async () => {
    if (!auction || draftLots.length === 0) return;

    /*
     * A lot with no title reaches the catalogue as a blank row. Rather than
     * silently skipping those — which would recreate the very bug this fixes,
     * just with a different lot going missing — say how many there are and let
     * the auctioneer decide. Their sale, their call.
     */
    const untitled = draftLots.filter((l) => !l.title?.trim()).length;
    const lotWord = draftLots.length === 1 ? 'lot' : 'lots';
    const warning =
      untitled > 0
        ? `\n\n${String(untitled)} of them ${untitled === 1 ? 'has' : 'have'} no title yet and will appear in the catalogue untitled. Cancel if you would rather finish those first.`
        : '';
    if (
      !window.confirm(
        `Schedule ${String(draftLots.length)} draft ${lotWord}?\n\nThey become part of the published catalogue and will open for bidding when this auction goes live.${warning}`,
      )
    ) {
      return;
    }

    setPromotingDrafts(true);
    const { data, error: err } = await supabase
      .from('lots')
      .update({ status: 'scheduled' })
      .eq('auction_id', auction.id)
      .eq('status', 'draft')
      .select('id');
    setPromotingDrafts(false);

    if (err) {
      toast.error(`Could not schedule the lots: ${err.message}`);
      return;
    }
    const moved = data?.length ?? 0;
    toast.success(`Scheduled ${String(moved)} ${moved === 1 ? 'lot' : 'lots'}.`);
    await refreshLots();
  }, [auction, draftLots, refreshLots]);

  const setField = useCallback(<K extends keyof FormState>(key: K, value: FormState[K]) => {
    setForm((f) => ({ ...f, [key]: value }));
  }, []);

  /*
   * The collection scheduler's three operations. Rows are edited in place and
   * only sorted on the way to the database — re-sorting as the auctioneer types
   * would move the row out from under the cursor the moment a date became
   * valid.
   */
  const addWindow = useCallback(() => {
    setForm((f) => ({ ...f, pickup_windows: [...f.pickup_windows, { ...EMPTY_WINDOW_ROW }] }));
  }, []);

  const removeWindow = useCallback((index: number) => {
    setForm((f) => ({
      ...f,
      pickup_windows: f.pickup_windows.filter((_, i) => i !== index),
    }));
  }, []);

  const setWindowField = useCallback((index: number, key: keyof PickupWindowRow, value: string) => {
    setForm((f) => ({
      ...f,
      pickup_windows: f.pickup_windows.map((w, i) => (i === index ? { ...w, [key]: value } : w)),
    }));
  }, []);

  /*
   * Shown live beside the rows, not only on submit: a house filling in three
   * collection days should see the second one is wrong while looking at it.
   */
  const windowErrors = useMemo(
    () => validatePickupWindows(form.pickup_windows, resolveTimeZone(form.timezone)),
    [form.pickup_windows, form.timezone],
  );

  /*
   * The short name shown beside every date on this form — "CDT", "JST".
   *
   * Taken at the sale's START where there is one, because that is the clock the
   * house is thinking in, and because the abbreviation is SEASONAL: a sale in
   * March is CST or CDT depending on the day, and a fixed label would be wrong
   * for half the year.
   */
  const zoneLabel = useMemo(() => {
    const tz = resolveTimeZone(form.timezone);
    const at = zonedLocalToIso(form.starts_at, tz) ?? new Date().toISOString();
    return zoneAbbreviation(at, tz);
  }, [form.timezone, form.starts_at]);

  /*
   * A stored zone that is not in the curated list is still legitimate — a house
   * may have an unusual one — so it is added rather than silently replaced by
   * the first option, which would change the sale's times on the next save.
   */
  const zoneOptions = useMemo(() => {
    const current = resolveTimeZone(form.timezone);
    return COMMON_TIME_ZONES.some((z) => z.id === current)
      ? COMMON_TIME_ZONES
      : [{ id: current, label: current }, ...COMMON_TIME_ZONES];
  }, [form.timezone]);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);

    if (!form.title.trim()) {
      setError('Title is required.');
      return;
    }

    /*
     * 🔴 A BAD COLLECTION WINDOW BLOCKS THE SAVE rather than being dropped on
     * the way past. The reader drops malformed entries silently and is right to
     * — on a bidder's phone that costs a hidden button. Here the same silence
     * would let a house leave believing collection was scheduled, and find out
     * when a buyer reached a locked yard.
     */
    if (windowErrors.length > 0) {
      const first = windowErrors[0];
      setError(
        `Collection time ${String((first?.index ?? 0) + 1)}: ${first?.message ?? 'is incomplete.'}`,
      );
      return;
    }

    const resolvedTenantId = !isCreate
      ? (auction?.tenant_id ?? claims.tenantId ?? '')
      : (claims.tenantId ?? selectedTenantId);

    if (!resolvedTenantId) {
      setError(
        needsTenantPicker
          ? 'Pick a tenant to create this auction under.'
          : 'Your account has no tenant context.',
      );
      return;
    }

    setSaving(true);
    const payload = formToRow(form, resolvedTenantId);

    if (isCreate) {
      const { data, error: err } = await supabase
        .from('auctions')
        .insert(payload)
        .select('id')
        .single();
      setSaving(false);
      if (err) setError(err.message);
      else void navigate(`/admin/auctions/${data.id}`, { replace: true });
      return;
    }

    if (!id) {
      setSaving(false);
      return;
    }

    const { error: err } = await supabase.from('auctions').update(payload).eq('id', id);
    setSaving(false);
    if (err) {
      setError(err.message);
    } else {
      toast.success('Changes saved.');
      // Refetch so aggregates (lot_count) update
      const { data: fresh } = await supabase
        .from('auctions')
        .select(AUCTION_ROW_COLUMNS)
        .eq('id', id)
        .maybeSingle();
      if (fresh) setAuction(fresh);
    }
  }

  const displayTitle = useMemo(() => {
    if (isCreate) return 'New auction';
    return form.title || '(untitled auction)';
  }, [isCreate, form.title]);

  if (loading) {
    return <div style={{ padding: '2rem', color: 'var(--text-muted)' }}>Loading auction…</div>;
  }

  return (
    <div>
      <header style={{ marginBottom: '1.5rem' }}>
        <div style={{ marginBottom: '0.5rem' }}>
          <Link to="/admin/auctions" style={backLinkStyle}>
            ← Auctions
          </Link>
        </div>
        <div
          style={{
            display: 'flex',
            alignItems: 'flex-end',
            justifyContent: 'space-between',
            gap: '1rem',
            flexWrap: 'wrap',
          }}
        >
          <div>
            <h1
              style={{
                fontFamily: 'var(--font-heading-active)',
                fontSize: '1.875rem',
                fontWeight: 700,
                color: 'var(--text)',
                marginBottom: '0.25rem',
              }}
            >
              {displayTitle}
            </h1>
            {auction && (
              <div
                style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', flexWrap: 'wrap' }}
              >
                <Badge variant={auction.status === 'active' ? 'success' : 'outline'}>
                  {auction.status}
                </Badge>
                <Badge variant="outline">{auction.auction_type.replace(/_/g, ' ')}</Badge>
                <span style={{ fontSize: '0.8rem', color: 'var(--text-muted)' }}>
                  {auction.lot_count} lot{auction.lot_count === 1 ? '' : 's'}
                </span>
              </div>
            )}
          </div>
          <div
            style={{
              display: 'flex',
              gap: '1rem',
              alignItems: 'center',
              flexWrap: 'wrap',
            }}
          >
            {auction && auction.slug && (
              <a
                href={`/auctions/${auction.slug}`}
                target="_blank"
                rel="noreferrer"
                style={{
                  fontSize: '0.85rem',
                  color: 'var(--brand-primary)',
                  textDecoration: 'none',
                }}
              >
                👁 Preview as bidder →
              </a>
            )}
            <LearnMoreLink helpSlug="auction-setup" newTab>
              Setup guide
            </LearnMoreLink>
            {auction && auction.requires_bidder_approval && (
              <Link
                to={`/admin/auctions/${auction.id}/approvals`}
                style={{ color: 'var(--brand-accent)', fontSize: '0.875rem' }}
              >
                Bidder approvals →
              </Link>
            )}
            {auction && (
              <Link
                to={`/admin/auctions/${auction.id}/auctioneer`}
                style={{ color: 'var(--brand-accent)', fontSize: '0.875rem' }}
              >
                Auctioneer console →
              </Link>
            )}
          </div>
        </div>
      </header>

      {error && <AlertBox variant="danger">{error}</AlertBox>}

      {needsTenantPicker && isCreate && (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '0.75rem',
            padding: '0.75rem 1rem',
            marginBottom: '1rem',
            borderRadius: 'var(--r-tile, 0.375rem)',
            background: 'var(--surface-muted, #fef3c7)',
            border: '1px solid var(--admin-hairline, var(--border))',
            fontSize: '0.875rem',
            flexWrap: 'wrap',
          }}
        >
          <Badge variant="outline">Super admin</Badge>
          <span>Tenant:</span>
          <select
            value={selectedTenantId}
            onChange={(e) => setSelectedTenantId(e.target.value)}
            style={smallSelectStyle}
          >
            <option value="">— Pick a tenant —</option>
            {tenantOptions.map((t) => (
              <option key={t.id} value={t.id}>
                {t.name} ({t.slug})
              </option>
            ))}
          </select>
        </div>
      )}

      <form
        onSubmit={(e) => {
          void handleSubmit(e);
        }}
        style={{ display: 'grid', gap: '1.5rem' }}
      >
        <Section title="Basics">
          <Field label="Title" required>
            <input
              type="text"
              required
              value={form.title}
              onChange={(e) => {
                setField('title', e.target.value);
                if (!auction && !form.slug) setField('slug', slugify(e.target.value));
              }}
              style={inputStyle}
            />
          </Field>
          <TwoCol>
            <Field
              label="Slug"
              help={
                <HelpTooltip helpSlug="auction-setup">
                  URL-safe identifier. Auto-derived from the title; override if needed.
                </HelpTooltip>
              }
            >
              <input
                type="text"
                value={form.slug}
                onChange={(e) => setField('slug', slugify(e.target.value))}
                style={inputStyle}
              />
            </Field>
            <Field label="Reference number (internal)">
              <input
                type="number"
                value={form.reference_number}
                onChange={(e) => setField('reference_number', e.target.value)}
                style={inputStyle}
              />
            </Field>
            <Field
              label="Photo file prefix"
              help={
                <HelpTooltip helpSlug="cataloging-workflow">
                  Short tag (e.g. DTA26A) baked into every photo filename for this auction:{' '}
                  <code>{'{prefix}-{lot#}-{seq}.jpg'}</code>. 2-24 chars,
                  letters/digits/underscore/hyphen. Unique within your tenant.
                </HelpTooltip>
              }
            >
              <input
                type="text"
                value={form.photo_file_prefix}
                onChange={(e) => setField('photo_file_prefix', e.target.value)}
                placeholder="e.g. DTA26A"
                pattern="^[A-Za-z0-9][A-Za-z0-9_-]{1,23}$"
                style={{ ...inputStyle, fontFamily: 'var(--font-mono, monospace)' }}
              />
            </Field>
            <Field label="Auction type">
              <select
                value={form.auction_type}
                onChange={(e) => setField('auction_type', e.target.value)}
                style={inputStyle}
              >
                {AUCTION_TYPES.map((t) => (
                  <option key={t} value={t}>
                    {t.replace(/_/g, ' ')}
                  </option>
                ))}
              </select>
            </Field>
            <Field label="Category">
              <select
                value={form.category_id}
                onChange={(e) => setField('category_id', e.target.value)}
                style={inputStyle}
              >
                <option value="">— None —</option>
                {categories.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name}
                  </option>
                ))}
              </select>
            </Field>
          </TwoCol>
          <Field label="Description">
            <textarea
              value={form.description}
              onChange={(e) => setField('description', e.target.value)}
              rows={3}
              style={{ ...inputStyle, resize: 'vertical', minHeight: '4rem' }}
            />
          </Field>
          <Field label="Preview text (short summary on lists)">
            <input
              type="text"
              value={form.preview_text}
              onChange={(e) => setField('preview_text', e.target.value)}
              style={inputStyle}
            />
          </Field>
        </Section>

        <Section title="Schedule">
          <Field
            label="Time zone for this sale"
            help={
              <HelpTooltip helpSlug="auction-setup">
                Every time on this page — the sale, the preview and collection — is entered and
                shown in this zone, so the same number means the same moment wherever you are when
                you type it.
              </HelpTooltip>
            }
          >
            <select
              value={resolveTimeZone(form.timezone)}
              onChange={(e) => setField('timezone', e.target.value)}
              style={inputStyle}
            >
              {zoneOptions.map((z) => (
                <option key={z.id} value={z.id}>
                  {z.label}
                </option>
              ))}
            </select>
          </Field>
          {/* 🔴 CHANGING THE ZONE KEEPS THE TYPING, NOT THE INSTANT. Somebody
              changing this is almost always saying "I had the wrong zone; the
              times I entered are right" — so 6pm stays 6pm and now means 6pm
              somewhere else. Said out loud because the opposite reading is just
              as defensible and the difference is hours. */}
          <p style={sectionHintStyle}>
            Times below stay as you typed them if you change this — 6:00 PM stays 6:00 PM, in the
            new zone.
          </p>
          <TwoCol>
            <Field label={zoneLabel === '' ? 'Starts at' : `Starts at (${zoneLabel})`}>
              <input
                type="datetime-local"
                value={form.starts_at}
                onChange={(e) => setField('starts_at', e.target.value)}
                style={inputStyle}
              />
            </Field>
            <Field label={zoneLabel === '' ? 'Ends at' : `Ends at (${zoneLabel})`}>
              <input
                type="datetime-local"
                value={form.ends_at}
                onChange={(e) => setField('ends_at', e.target.value)}
                style={inputStyle}
              />
            </Field>
            <Field
              label={
                zoneLabel === ''
                  ? 'Preview starts (optional)'
                  : `Preview starts (optional, ${zoneLabel})`
              }
            >
              <input
                type="datetime-local"
                value={form.preview_starts_at}
                onChange={(e) => setField('preview_starts_at', e.target.value)}
                style={inputStyle}
              />
            </Field>
            <Field
              label={
                zoneLabel === ''
                  ? 'Preview ends (optional)'
                  : `Preview ends (optional, ${zoneLabel})`
              }
            >
              <input
                type="datetime-local"
                value={form.preview_ends_at}
                onChange={(e) => setField('preview_ends_at', e.target.value)}
                style={inputStyle}
              />
            </Field>
          </TwoCol>
        </Section>

        <Section title="Staggered close">
          <TwoCol>
            <Field
              label="Lots per wave"
              help={
                <HelpTooltip helpSlug="staggered-closes">
                  How many lots close in each wave. DTA uses 6.
                </HelpTooltip>
              }
            >
              <input
                type="number"
                min={0}
                value={form.stagger_qty}
                onChange={(e) => setField('stagger_qty', e.target.value)}
                style={inputStyle}
              />
            </Field>
            <Field
              label="Seconds between waves"
              help={
                <HelpTooltip helpSlug="staggered-closes">
                  DTA uses 60 seconds (one wave per minute).
                </HelpTooltip>
              }
            >
              <input
                type="number"
                min={0}
                value={form.stagger_seconds}
                onChange={(e) => setField('stagger_seconds', e.target.value)}
                style={inputStyle}
              />
            </Field>
          </TwoCol>
        </Section>

        <Section title="Anti-snipe">
          <TwoCol>
            <Field
              label="Threshold (seconds)"
              help={
                <HelpTooltip helpSlug="anti-snipe">
                  Bids within this many seconds of close trigger an extension.
                </HelpTooltip>
              }
            >
              <input
                type="number"
                min={0}
                value={form.extension_threshold_seconds}
                onChange={(e) => setField('extension_threshold_seconds', e.target.value)}
                style={inputStyle}
              />
            </Field>
            <Field
              label="Amount (seconds)"
              help={
                <HelpTooltip helpSlug="anti-snipe">
                  How much to extend. Typically matches the threshold.
                </HelpTooltip>
              }
            >
              <input
                type="number"
                min={0}
                value={form.extension_amount_seconds}
                onChange={(e) => setField('extension_amount_seconds', e.target.value)}
                style={inputStyle}
              />
            </Field>
            <Field
              label="Method"
              help={
                <HelpTooltip helpSlug="anti-snipe">
                  &apos;Reset&apos; pushes ends_at forward from the bid time. &apos;Extend&apos;
                  adds the amount to the current ends_at.
                </HelpTooltip>
              }
            >
              <select
                value={form.extension_method}
                onChange={(e) => setField('extension_method', e.target.value)}
                style={inputStyle}
              >
                {EXTENSION_METHODS.map((m) => (
                  <option key={m} value={m}>
                    {m}
                  </option>
                ))}
              </select>
            </Field>
          </TwoCol>
        </Section>

        <Section title="Lot defaults">
          <TwoCol>
            <Field label="Default starting bid">
              <div style={{ position: 'relative' }}>
                <span
                  style={{
                    position: 'absolute',
                    left: '0.75rem',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    color: 'var(--text-muted)',
                    fontSize: '0.875rem',
                  }}
                >
                  $
                </span>
                <input
                  type="text"
                  inputMode="decimal"
                  value={form.default_starting_bid}
                  onChange={(e) => setField('default_starting_bid', e.target.value)}
                  style={{ ...inputStyle, paddingLeft: '1.5rem' }}
                />
              </div>
            </Field>
            <Field label="Default bid increment scale">
              <select
                value={form.default_bid_increment_scale_id}
                onChange={(e) => setField('default_bid_increment_scale_id', e.target.value)}
                style={inputStyle}
              >
                <option value="">— Tenant default —</option>
                {scales.map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.name}
                    {s.is_default ? ' (default)' : ''}
                  </option>
                ))}
              </select>
            </Field>
            <Field
              label="Bid lock mode"
              help={
                <HelpTooltip helpSlug="auction-setup">
                  &apos;Increment absolute&apos; forces each bid to the exact next increment.
                  &apos;Any higher amount&apos; accepts any amount above current + increment.
                </HelpTooltip>
              }
            >
              <select
                value={form.lock_bid_amount}
                onChange={(e) => setField('lock_bid_amount', e.target.value)}
                style={inputStyle}
              >
                {LOCK_MODES.map((m) => (
                  <option key={m} value={m}>
                    {m.replace(/_/g, ' ')}
                  </option>
                ))}
              </select>
            </Field>
          </TwoCol>
        </Section>

        <Section title="Premiums">
          <TwoCol>
            <Field
              label="Buyer's premium (%)"
              help={
                <HelpTooltip helpSlug="auction-setup">
                  Added to hammer price. Stored as basis points (1500 = 15.00%).
                </HelpTooltip>
              }
            >
              <input
                type="text"
                inputMode="decimal"
                value={form.buyers_premium_pct}
                onChange={(e) => setField('buyers_premium_pct', e.target.value)}
                style={inputStyle}
              />
            </Field>
            <Field label="Online premium (%) — optional">
              <input
                type="text"
                inputMode="decimal"
                value={form.online_premium_pct}
                onChange={(e) => setField('online_premium_pct', e.target.value)}
                style={inputStyle}
              />
            </Field>
          </TwoCol>
        </Section>

        <Section title="Consignment &amp; Tax">
          <TwoCol>
            <Field
              label="Consignment profile"
              help={
                <HelpTooltip helpSlug="auction-setup">
                  Default seller terms (commission + fees) for this auction&apos;s lots, used at
                  settlement. Overridable per consignor or lot.
                </HelpTooltip>
              }
            >
              <select
                value={form.consignment_profile_id}
                onChange={(e) => setField('consignment_profile_id', e.target.value)}
                style={inputStyle}
              >
                <option value="">— Tenant default —</option>
                {consignProfiles.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.name}
                    {p.is_default ? ' (default)' : ''}
                  </option>
                ))}
              </select>
            </Field>
            <Field
              label="Tax profile"
              help={
                <HelpTooltip helpSlug="auction-setup">
                  When set, sales tax is computed and captured per invoice line at invoice
                  generation. Leave blank for no tax.
                </HelpTooltip>
              }
            >
              <select
                value={form.tax_profile_id}
                onChange={(e) => setField('tax_profile_id', e.target.value)}
                style={inputStyle}
              >
                <option value="">— None —</option>
                {taxProfiles.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.name}
                    {p.is_default ? ' (default)' : ''}
                  </option>
                ))}
              </select>
            </Field>
          </TwoCol>
        </Section>

        <Section title="Buyer controls">
          <label
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
              fontSize: '0.9rem',
              color: 'var(--text)',
            }}
          >
            <input
              type="checkbox"
              checked={form.requires_bidder_approval}
              onChange={(e) => setField('requires_bidder_approval', e.target.checked)}
            />
            Require bidder approval before bidding
            <HelpTooltip helpSlug="requesting-approval">
              Bidders submit a request; tenant staff approve or reject at
              /admin/auctions/:id/approvals. Enforced server-side in place_bid.
            </HelpTooltip>
          </label>
          <label
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
              fontSize: '0.9rem',
              color: 'var(--text)',
            }}
          >
            <input
              type="checkbox"
              checked={form.allow_preview_bidding}
              onChange={(e) => setField('allow_preview_bidding', e.target.checked)}
            />
            Allow preview bidding
            <HelpTooltip helpSlug="preview-bidding">
              Lets bidders place bids on scheduled lots before the auction opens (a preview/pre-bid
              window). Off by default: only active lots take bids. Enforced server-side in
              place_bid.
            </HelpTooltip>
          </label>
          <label
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
              fontSize: '0.9rem',
              color: 'var(--text)',
            }}
          >
            <input
              type="checkbox"
              checked={form.requires_terms_acceptance}
              onChange={(e) => setField('requires_terms_acceptance', e.target.checked)}
            />
            Require terms acceptance before first bid
          </label>
          <label
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
              fontSize: '0.9rem',
              color: 'var(--text)',
            }}
          >
            <input
              type="checkbox"
              checked={form.requires_cc_preauth}
              onChange={(e) => setField('requires_cc_preauth', e.target.checked)}
            />
            Require credit-card pre-authorization
            <HelpTooltip helpSlug="auction-setup">
              Stores a card and places a hold before bidding opens. Wired through Stripe in Module
              #9.
            </HelpTooltip>
          </label>
          {form.requires_cc_preauth && (
            <TwoCol>
              <Field label="Pre-auth hold amount">
                <div style={{ position: 'relative' }}>
                  <span
                    style={{
                      position: 'absolute',
                      left: '0.75rem',
                      top: '50%',
                      transform: 'translateY(-50%)',
                      color: 'var(--text-muted)',
                      fontSize: '0.875rem',
                    }}
                  >
                    $
                  </span>
                  <input
                    type="text"
                    inputMode="decimal"
                    value={form.cc_preauth_amount}
                    onChange={(e) => setField('cc_preauth_amount', e.target.value)}
                    style={{ ...inputStyle, paddingLeft: '1.5rem' }}
                    placeholder="100.00"
                  />
                </div>
              </Field>
            </TwoCol>
          )}
        </Section>

        {/* 🔴 THE COLUMNS BEHIND THIS SECTION HAVE EXISTED SINCE 20260415000007
            AND NOTHING HAS EVER WRITTEN THEM. The Module #4 changelog recorded
            the gap — "DB fields exist; form doesn't expose them yet" — and every
            surface that shows collection details has been reading columns no
            screen could fill. `pickup_dates` in particular had a documented
            contract, a tolerant reader and a default of `[]` on every row, so
            `resolvePickup().nextWindow` was null everywhere and the calendar
            entry, the reminder and the collect-by countdown all stayed hidden.
            This is the writer they were waiting for. */}
        <Section title="Pickup &amp; collection">
          <p style={sectionHintStyle}>
            Where and when buyers collect what they win. Per sale, not per house — a sale that runs
            out of a different warehouse says so here. Shown on the lot page, the sale terms, the
            invoice and the app&rsquo;s Details tab.{' '}
            <LearnMoreLink helpSlug="auction-pickup" newTab>
              Setting collection times
            </LearnMoreLink>
          </p>

          <Field
            label="Location name"
            help={
              <HelpTooltip helpSlug="auction-pickup">
                A named place a person could be told over the phone — &ldquo;Bay 3, rear
                yard&rdquo;. Shown above the address.
              </HelpTooltip>
            }
          >
            <input
              type="text"
              value={form.pickup_location}
              onChange={(e) => setField('pickup_location', e.target.value)}
              placeholder="Main warehouse"
              style={inputStyle}
            />
          </Field>

          <TwoCol>
            <Field label="Address line 1">
              <input
                type="text"
                value={form.pickup_address_line1}
                onChange={(e) => setField('pickup_address_line1', e.target.value)}
                style={inputStyle}
              />
            </Field>
            <Field label="Address line 2">
              <input
                type="text"
                value={form.pickup_address_line2}
                onChange={(e) => setField('pickup_address_line2', e.target.value)}
                style={inputStyle}
              />
            </Field>
            <Field label="City">
              <input
                type="text"
                value={form.pickup_city}
                onChange={(e) => setField('pickup_city', e.target.value)}
                style={inputStyle}
              />
            </Field>
            <Field label="State / region">
              <input
                type="text"
                value={form.pickup_region}
                onChange={(e) => setField('pickup_region', e.target.value)}
                style={inputStyle}
              />
            </Field>
            <Field label="Postal code">
              <input
                type="text"
                value={form.pickup_postal_code}
                onChange={(e) => setField('pickup_postal_code', e.target.value)}
                style={inputStyle}
              />
            </Field>
          </TwoCol>

          {/* The app drops a map pin on this address, geocoded on the device.
              A blank address is not an error — plenty of sales give only a
              location name — but it is worth saying what is lost. */}
          <Field
            label="Collection instructions"
            help={
              <HelpTooltip helpSlug="auction-pickup">
                Anything else a buyer needs: access, parking, what to bring, help loading.
              </HelpTooltip>
            }
          >
            <textarea
              rows={3}
              value={form.pickup_instructions}
              onChange={(e) => setField('pickup_instructions', e.target.value)}
              placeholder="Bring your own packing materials. Loading assistance available for large items."
              style={{ ...inputStyle, resize: 'vertical' }}
            />
          </Field>

          <div style={{ marginTop: '0.5rem' }}>
            <div
              style={{
                display: 'flex',
                alignItems: 'baseline',
                justifyContent: 'space-between',
                gap: '1rem',
                flexWrap: 'wrap',
              }}
            >
              <span style={{ fontSize: '0.8rem', fontWeight: 500, color: 'var(--text)' }}>
                Collection times
              </span>
              <Button type="button" variant="secondary" size="sm" onClick={addWindow}>
                + Add a collection time
              </Button>
            </div>

            {/* 🔴 WHY DATED WINDOWS EXIST ALONGSIDE THE PROSE ABOVE. "Sat 9–4"
                cannot say WHICH Saturday, so nothing can be built on it. A dated
                window can: it is what turns on the add-to-calendar button, and
                later the pickup reminder and the collect-by countdown. Prose
                that cannot name an absolute date belongs in the instructions
                field, which is why both are here and neither replaces the
                other. */}
            {/* 🔴 THE PLAIN-ENGLISH VERSION OF WHY BOTH EXIST. Jim, 2026-09-18:
                *"I don't know what 'dated window' is."* Fair — it was our
                word, not a house's. What a house needs to know is that a time
                with a real date on it can go in a buyer's calendar and "Saturday
                9–4" cannot, because it never says WHICH Saturday. */}
            <p style={sectionHintStyle}>
              Give a real date and time and buyers get an <strong>Add to calendar</strong> button.
              Typing &ldquo;Saturday 9–4&rdquo; in the instructions above cannot do that, because it
              does not say which Saturday. Leave this empty if words suit your sale better — both
              are shown.
            </p>

            {form.pickup_windows.length === 0 ? (
              <p style={{ ...sectionHintStyle, fontStyle: 'italic' }}>No collection times set.</p>
            ) : (
              <div style={{ display: 'grid', gap: '0.75rem' }}>
                {form.pickup_windows.map((w, index) => {
                  const rowError = windowErrors.find((e) => e.index === index);
                  return (
                    <div
                      key={index}
                      style={{
                        border: `1px solid ${
                          rowError
                            ? 'var(--border-danger, #fecaca)'
                            : 'var(--admin-hairline, var(--border))'
                        }`,
                        borderRadius: 'var(--r-tile, 0.375rem)',
                        padding: '0.75rem',
                        background: 'var(--surface)',
                        display: 'grid',
                        gap: '0.5rem',
                      }}
                    >
                      <TwoCol>
                        <Field
                          label={zoneLabel === '' ? 'Starts' : `Starts (${zoneLabel})`}
                          required
                        >
                          <input
                            type="datetime-local"
                            value={w.startsAt}
                            onChange={(e) => setWindowField(index, 'startsAt', e.target.value)}
                            style={inputStyle}
                          />
                        </Field>
                        <Field
                          label={
                            zoneLabel === '' ? 'Ends (optional)' : `Ends (optional, ${zoneLabel})`
                          }
                        >
                          <input
                            type="datetime-local"
                            value={w.endsAt}
                            onChange={(e) => setWindowField(index, 'endsAt', e.target.value)}
                            style={inputStyle}
                          />
                        </Field>
                      </TwoCol>
                      <Field label="Note for this window (optional)">
                        <input
                          type="text"
                          value={w.note}
                          onChange={(e) => setWindowField(index, 'note', e.target.value)}
                          placeholder="Ring the bell at the yard gate"
                          style={inputStyle}
                        />
                      </Field>
                      <div
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          gap: '0.75rem',
                          flexWrap: 'wrap',
                        }}
                      >
                        <span
                          style={{
                            fontSize: '0.8rem',
                            color: rowError ? 'var(--text-danger, #991b1b)' : 'var(--text-muted)',
                          }}
                        >
                          {rowError?.message ?? ''}
                        </span>
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => {
                            removeWindow(index);
                          }}
                        >
                          Remove
                        </Button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </Section>

        {/* These three have existed on `auctions` since 20260415000007 and, until
            now, only `hide_bid_history` was read anywhere and none of them could
            be set — the settings promised something the product did not do.
            They are grouped by what a bidder sees rather than by when it applies,
            because a house thinks "what do I publish?", not "which surface?". */}
        <Section title="What bidders see">
          <label style={checkboxRowStyle}>
            <input
              type="checkbox"
              checked={!form.hide_unsold_lots}
              onChange={(e) => setField('hide_unsold_lots', !e.target.checked)}
            />
            Publish unsold lots in the sale results
            <HelpTooltip helpSlug="sale-results">
              Off by default. On, an ended sale&rsquo;s results page shows the full run order with
              an outcome for every lot — sold, reserve not met, no bids — and offers a sold/unsold
              filter. Off, it lists only what sold. A bidder always sees their own result either
              way.
            </HelpTooltip>
          </label>
          <label style={checkboxRowStyle}>
            <input
              type="checkbox"
              checked={form.hide_winning_info}
              onChange={(e) => setField('hide_winning_info', e.target.checked)}
            />
            Hide winning amounts from other bidders
            <HelpTooltip helpSlug="sale-results">
              Hides what lots sold for, on the lot page while bidding is open and on the results
              page after it closes. The sale total goes with it. Bid counts, outcomes and every
              bidder&rsquo;s own figures are unaffected.
            </HelpTooltip>
          </label>
          <label style={checkboxRowStyle}>
            <input
              type="checkbox"
              checked={form.hide_bid_history}
              onChange={(e) => setField('hide_bid_history', e.target.checked)}
            />
            Hide the bid-by-bid history
            <HelpTooltip helpSlug="sale-results">
              Removes the trail on the lot page and the bid counts on the results page. Bidders
              still see their own bids. The live bid count in the heading stays.
            </HelpTooltip>
          </label>
          <p style={{ fontSize: '0.8rem', color: 'var(--text-muted)', margin: '0.25rem 0 0' }}>
            Bidders are never named to each other, whatever is set here, and maximum bids are never
            shown to anyone.
          </p>
        </Section>

        <Section title="Status">
          <TwoCol>
            <Field
              label="Status"
              help={
                <HelpTooltip helpSlug="auction-statuses">
                  Transitions can be set manually. Platform cron moves active→completed as lots
                  close; &ldquo;closing&rdquo; is a derived urgency state, never stored.
                </HelpTooltip>
              }
            >
              <select
                value={form.status}
                onChange={(e) => setField('status', e.target.value)}
                style={inputStyle}
              >
                {STATUSES.map((s) => (
                  <option key={s} value={s}>
                    {s}
                  </option>
                ))}
              </select>
            </Field>
          </TwoCol>
          <label
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
              fontSize: '0.9rem',
              color: 'var(--text)',
            }}
          >
            <input
              type="checkbox"
              checked={form.is_visible}
              onChange={(e) => setField('is_visible', e.target.checked)}
            />
            Visible to buyers (when status allows)
          </label>
        </Section>

        <div
          style={{
            display: 'flex',
            gap: '0.75rem',
            justifyContent: 'flex-end',
            alignItems: 'center',
            paddingTop: '0.5rem',
            borderTop: '1px solid var(--admin-hairline, var(--border))',
            flexWrap: 'wrap',
          }}
        >
          <Button type="button" variant="outline" asChild>
            <Link to="/admin/auctions">Cancel</Link>
          </Button>
          <Button type="submit" disabled={saving}>
            {saving ? 'Saving…' : isCreate ? 'Create auction' : 'Save changes'}
          </Button>
        </div>
      </form>

      {auction && (
        <>
          <section style={{ marginTop: '2.5rem' }}>
            <LiveStatusBanner status={live.status} timeZone={auction.timezone} audience="staff" />
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'flex-end',
                marginBottom: '1rem',
                flexWrap: 'wrap',
                gap: '0.5rem',
              }}
            >
              <div>
                <h2
                  style={{
                    fontSize: '1.25rem',
                    fontWeight: 600,
                    color: 'var(--text)',
                    marginBottom: '0.2rem',
                  }}
                >
                  Lots
                </h2>
                <LearnMoreLink helpSlug="lot-basics" newTab>
                  About lots
                </LearnMoreLink>
              </div>
              <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                {/* Shown only when there is something to do. A permanently
                    visible "Schedule 0 lots" is noise on every sale that has
                    already been scheduled. */}
                {draftLots.length > 0 && (
                  <Button
                    type="button"
                    onClick={() => void promoteDraftLots()}
                    disabled={promotingDrafts}
                  >
                    {promotingDrafts
                      ? 'Scheduling…'
                      : `Schedule ${String(draftLots.length)} draft ${draftLots.length === 1 ? 'lot' : 'lots'}`}
                  </Button>
                )}
                {/* The grid is a WINDOW of its own (Jim, 2026-10-06): the
                    office needs the photos on one screen and the spreadsheet
                    on the other, so this opens rather than navigates. */}
                {lots.length > 0 && (
                  <Button
                    type="button"
                    data-testid="open-lot-grid"
                    onClick={() => {
                      window.open(
                        `/admin/auctions/${auction.id}/grid`,
                        `lot-grid-${auction.id}`,
                        'noopener,noreferrer,width=1600,height=1000',
                      );
                    }}
                  >
                    ⊞ Edit lots in a grid
                  </Button>
                )}
                <Button type="button" variant="outline" onClick={() => setShowCreateEmpty(true)}>
                  + Create empty lots
                </Button>
                <Button type="button" variant="outline" onClick={() => setShowAddLots((v) => !v)}>
                  {showAddLots ? 'Close picker' : '+ Add from inventory'}
                </Button>
              </div>
            </div>

            {showAddLots && (
              <AddLotsFromInventory
                auctionId={auction.id}
                tenantId={auction.tenant_id}
                existingLotCount={lots.length}
                onAdded={() => {
                  setShowAddLots(false);
                  void refreshLots();
                }}
                onCancel={() => setShowAddLots(false)}
              />
            )}

            {lots.length > 0 && (
              <div
                style={{
                  display: 'flex',
                  gap: '0.35rem',
                  marginBottom: '0.6rem',
                  flexWrap: 'wrap',
                  fontSize: '0.8rem',
                }}
              >
                {(
                  [
                    { key: 'all', label: `All (${lots.length})` },
                    {
                      key: 'needs-research',
                      label: `Needs research (${lots.filter((l) => l.cataloged_at && !l.title).length})`,
                    },
                    {
                      key: 'needs-photos',
                      label: `Needs photos (${lots.filter((l) => !l.cataloged_at).length})`,
                    },
                    {
                      key: 'prices-to-review',
                      label: `Prices to review (${lots.filter((l) => draftStage(drafts.get(l.id) ?? null) === 'prices_to_review').length})`,
                    },
                    // Lots a worker added from the Cataloging app (2026-10-05) —
                    // the office's to review before publishing. Only offered
                    // when there are some; most houses add every lot here.
                    ...(lots.some((l) => l.added_in_warehouse_at !== null)
                      ? [
                          {
                            key: 'from-warehouse' as const,
                            label: `Added in the warehouse (${lots.filter((l) => l.added_in_warehouse_at !== null).length})`,
                          },
                        ]
                      : []),
                  ] as const
                ).map((opt) => (
                  <button
                    key={opt.key}
                    type="button"
                    onClick={() => setLotFilter(opt.key)}
                    style={{
                      padding: '0.3rem 0.6rem',
                      borderRadius: '999px',
                      border: '1px solid var(--admin-hairline, var(--border))',
                      background: lotFilter === opt.key ? 'var(--brand-primary)' : 'var(--surface)',
                      color:
                        lotFilter === opt.key ? 'var(--brand-primary-fg)' : 'var(--text-muted)',
                      fontSize: '0.75rem',
                      fontWeight: lotFilter === opt.key ? 600 : 500,
                      cursor: 'pointer',
                    }}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            )}

            {showCreateEmpty && (
              <CreateEmptyLotsModal
                auctionId={auction.id}
                photoFilePrefix={auction.photo_file_prefix ?? null}
                existingLotNumbers={lots.map((l) => l.lot_number)}
                onClose={() => setShowCreateEmpty(false)}
                onDone={(createdCount) => {
                  setShowCreateEmpty(false);
                  toast.success(
                    `Created ${createdCount} empty lot${createdCount === 1 ? '' : 's'}.`,
                  );
                  void refreshLots();
                }}
              />
            )}

            {lotsLoading ? (
              <div style={{ color: 'var(--text-muted)', fontSize: '0.875rem' }}>Loading lots…</div>
            ) : lots.length === 0 ? (
              <div
                style={{
                  padding: '2rem 1rem',
                  textAlign: 'center',
                  color: 'var(--text-muted)',
                  border: '1px dashed var(--border)',
                  borderRadius: 'var(--r-card, 0.5rem)',
                  fontSize: '0.875rem',
                }}
              >
                No lots yet. Click <strong>+ Add from inventory</strong> above.
              </div>
            ) : (
              <div
                style={{
                  border: '1px solid var(--admin-hairline, var(--border))',
                  borderRadius: 'var(--r-card, 0.5rem)',
                  overflow: 'hidden',
                  background: 'var(--surface-elev)',
                }}
              >
                <div style={{ overflowX: 'auto' }}>
                  <table
                    style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.875rem' }}
                  >
                    <thead>
                      <tr
                        style={{
                          background: 'var(--surface-muted)',
                          borderBottom: '1px solid var(--admin-hairline, var(--border))',
                        }}
                      >
                        <th style={thStyle}>Lot #</th>
                        <th style={thStyle}>Title</th>
                        <th style={{ ...thStyle, textAlign: 'right' }}>Qty</th>
                        <th style={{ ...thStyle, textAlign: 'right' }}>Starting bid</th>
                        <th style={{ ...thStyle, textAlign: 'right' }}>Current bid</th>
                        <th style={{ ...thStyle, textAlign: 'right' }}>Bids</th>
                        <th style={thStyle}>Status</th>
                        <th style={thStyle}>AI draft</th>
                      </tr>
                    </thead>
                    <tbody>
                      {shownLots
                        .filter((lot) => {
                          if (lotFilter === 'needs-research')
                            return lot.cataloged_at !== null && !lot.title;
                          if (lotFilter === 'needs-photos') return lot.cataloged_at === null;
                          if (lotFilter === 'prices-to-review')
                            return draftStage(drafts.get(lot.id) ?? null) === 'prices_to_review';
                          if (lotFilter === 'from-warehouse')
                            return lot.added_in_warehouse_at !== null;
                          return true;
                        })
                        .map((lot) => (
                          <tr
                            key={lot.id}
                            style={{
                              borderBottom: '1px solid var(--admin-hairline, var(--border))',
                            }}
                          >
                            <td style={tdStyle}>
                              <Link
                                to={`/admin/auctions/${auction.id}/lots/${lot.id}`}
                                style={{
                                  color: 'var(--brand-accent)',
                                  fontFamily: 'var(--font-mono, monospace)',
                                }}
                              >
                                {lot.lot_number}
                              </Link>
                            </td>
                            <td style={tdStyle}>
                              <Link
                                to={`/admin/auctions/${auction.id}/lots/${lot.id}`}
                                style={{ color: 'var(--text)' }}
                              >
                                {lot.title ?? '—'}
                              </Link>
                              {lot.added_in_warehouse_at !== null && (
                                <span style={{ marginLeft: '0.4rem' }}>
                                  <Badge variant="secondary">Added in the warehouse</Badge>
                                </span>
                              )}
                            </td>
                            <td style={{ ...tdStyle, textAlign: 'right' }}>{lot.quantity}</td>
                            <td
                              style={{
                                ...tdStyle,
                                textAlign: 'right',
                                fontVariantNumeric: 'tabular-nums',
                              }}
                            >
                              {formatCents(lot.starting_bid_cents)}
                            </td>
                            <td
                              style={{
                                ...tdStyle,
                                textAlign: 'right',
                                fontVariantNumeric: 'tabular-nums',
                              }}
                            >
                              {lot.current_bid_cents === null
                                ? '—'
                                : formatCents(lot.current_bid_cents)}
                            </td>
                            <td style={{ ...tdStyle, textAlign: 'right' }}>
                              {lot.bid_count ?? '—'}
                            </td>
                            <td style={tdStyle}>
                              <Badge variant="outline">{lot.status}</Badge>
                            </td>
                            <td style={tdStyle}>
                              {drafts.has(lot.id) ? (
                                <Badge
                                  variant={
                                    draftStage(drafts.get(lot.id) ?? null) === 'prices_to_review'
                                      ? 'warning'
                                      : draftStage(drafts.get(lot.id) ?? null) === 'failed'
                                        ? 'error'
                                        : 'secondary'
                                  }
                                >
                                  {DRAFT_STAGE_LABEL[draftStage(drafts.get(lot.id) ?? null)]}
                                </Badge>
                              ) : (
                                <span style={{ color: 'var(--text-muted)' }}>—</span>
                              )}
                            </td>
                          </tr>
                        ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </section>
          <ConsignorSettlementPanel auctionId={auction.id} auctionStatus={auction.status} />
          <AdminPreviewEvents auctionId={auction.id} />
        </>
      )}
    </div>
  );
}

function Section({ title, children }: { title: string; children: ReactNode }) {
  return (
    <section
      style={{
        border: '1px solid var(--admin-hairline, var(--border))',
        borderRadius: 'var(--r-card, 0.5rem)',
        background: 'var(--surface-elev)',
        padding: '1rem 1.25rem',
      }}
    >
      <h2
        style={{
          fontSize: '0.875rem',
          fontWeight: 600,
          textTransform: 'uppercase',
          letterSpacing: '0.04em',
          color: 'var(--text-muted)',
          marginBottom: '0.75rem',
        }}
      >
        {title}
      </h2>
      <div style={{ display: 'grid', gap: '0.75rem' }}>{children}</div>
    </section>
  );
}

function TwoCol({ children }: { children: ReactNode }) {
  return (
    <div
      style={{
        display: 'grid',
        gap: '0.75rem',
        gridTemplateColumns: 'repeat(auto-fit, minmax(14rem, 1fr))',
      }}
    >
      {children}
    </div>
  );
}

function Field({
  label,
  help,
  required,
  children,
}: {
  label: string;
  help?: ReactNode;
  required?: boolean;
  children: ReactNode;
}) {
  return (
    <label style={{ display: 'grid', gap: '0.35rem' }}>
      <span
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '0.35rem',
          fontSize: '0.8rem',
          fontWeight: 500,
          color: 'var(--text)',
        }}
      >
        {label}
        {required && <span style={{ color: 'var(--semantic-error, #b91c1c)' }}>*</span>}
        {help}
      </span>
      {children}
    </label>
  );
}

function AlertBox({ children }: { variant: 'danger'; children: ReactNode }) {
  const palette = {
    bg: 'var(--surface-danger, #fef2f2)',
    fg: 'var(--text-danger, #991b1b)',
    border: 'var(--border-danger, #fecaca)',
  };
  return (
    <div
      role="alert"
      style={{
        padding: '0.75rem 1rem',
        borderRadius: 'var(--r-tile, 0.375rem)',
        background: palette.bg,
        color: palette.fg,
        border: `1px solid ${palette.border}`,
        marginBottom: '1rem',
        fontSize: '0.875rem',
      }}
    >
      {children}
    </div>
  );
}

const thStyle: React.CSSProperties = {
  padding: '0.5rem 0.75rem',
  textAlign: 'left',
  fontWeight: 600,
  fontSize: '0.75rem',
  textTransform: 'uppercase',
  letterSpacing: '0.03em',
  color: 'var(--text-muted)',
};

const tdStyle: React.CSSProperties = {
  padding: '0.6rem 0.75rem',
  verticalAlign: 'middle',
};

const inputStyle: React.CSSProperties = {
  width: '100%',
  padding: '0.5rem 0.75rem',
  border: '1px solid var(--admin-hairline, var(--border))',
  borderRadius: 'var(--r-tile, 0.375rem)',
  background: 'var(--surface)',
  color: 'var(--text)',
  fontSize: '0.875rem',
  fontFamily: 'inherit',
};

// The same row the "Buyer controls" checkboxes have always used inline, named
// once so a new group cannot drift from the old one by a gap or a font size.
const checkboxRowStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: '0.5rem',
  fontSize: '0.9rem',
  color: 'var(--text)',
};

// Explanatory line under a section heading or a group label. Muted and small so
// it reads as guidance rather than as another field.
const sectionHintStyle: React.CSSProperties = {
  fontSize: '0.8rem',
  color: 'var(--text-muted)',
  margin: '0 0 0.25rem',
  lineHeight: 1.5,
};

const smallSelectStyle: React.CSSProperties = {
  padding: '0.35rem 0.6rem',
  borderRadius: 'var(--r-tile, 0.375rem)',
  border: '1px solid var(--admin-hairline, var(--border))',
  background: 'var(--surface)',
  color: 'var(--text)',
  fontSize: '0.875rem',
};

const backLinkStyle: React.CSSProperties = {
  color: 'var(--brand-accent)',
  fontSize: '0.875rem',
  textDecoration: 'none',
};
